import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  Alert,
  Link,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import SimpleInput from 'components/SimpleInput';
import { useCustomToast } from 'hooks';
import { useAtualizacaoCadastralMutation } from 'setup/authOverrides';
import {
  PDC_CENTRAL_ATENDIMENTO_0800,
  PDC_CENTRAL_WHATSAPP_DISPLAY,
  PDC_CENTRAL_WHATSAPP_URL,
} from 'utils/constants';
import { isValidCPF } from 'utils/validations';
import * as z from 'zod';

const SUPPORT_ERROR_LEAD =
  'Não foi possível concluir a solicitação. Verifique se o CPF e o e-mail estão corretos. Se o problema continuar, procure o suporte.';

const supportContactPlain =
  ` Telefone ${PDC_CENTRAL_ATENDIMENTO_0800} ou WhatsApp ${PDC_CENTRAL_WHATSAPP_DISPLAY}.`;

function SupportContactLinks() {
  return (
    <Text as="span">
      {' '}
      Se precisar de ajuda, contate o suporte pelo telefone{' '}
      <Link href="tel:08000254141" fontWeight="semibold" textDecoration="underline">
        {PDC_CENTRAL_ATENDIMENTO_0800}
      </Link>
      {' ou pelo '}
      <Link
        href={PDC_CENTRAL_WHATSAPP_URL}
        isExternal
        fontWeight="semibold"
        textDecoration="underline"
      >
        WhatsApp {PDC_CENTRAL_WHATSAPP_DISPLAY}
      </Link>
      .
    </Text>
  );
}

interface AtualizacaoCadastralModalProps {
  isOpen: boolean;
  onClose: () => void;
  cpfInicial?: string;
}

interface FormValues {
  cpf: string;
  email: string;
}

const schema = z.object({
  email: z
    .string()
    .email('E-mail inválido')
    .nonempty('E-mail não pode ser vazio'),
  cpf: z
    .string()
    .min(14, { message: 'CPF inválido' })
    .nonempty('CPF não pode ser vazio')
    .refine(value => isValidCPF(value), 'CPF inválido'),
});

export const AtualizacaoCadastralModal = ({
  isOpen,
  onClose,
  cpfInicial,
}: AtualizacaoCadastralModalProps) => {
  const { handleToast } = useCustomToast();
  const [atualizarCadastro, { isLoading }] = useAtualizacaoCadastralMutation();
  const [submitError, setSubmitError] = useState<string>();

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (isOpen && cpfInicial) {
      setValue('cpf', cpfInicial);
    }
    if (isOpen) {
      setSubmitError(undefined);
    }
  }, [cpfInicial, isOpen, setValue]);

  const handleClose = useCallback(() => {
    reset();
    setSubmitError(undefined);
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async (data: FormValues) => {
      setSubmitError(undefined);
      try {
        const response = await atualizarCadastro({
          cpf: data.cpf,
          email: data.email,
        }).unwrap();

        handleToast({
          step: 5,
          action: 'E-mail enviado!',
          successText:
            response.message ||
            'Enviamos uma senha provisória para o e-mail informado. Utilize-a para entrar e defina uma nova senha.',
        });
        handleClose();
      } catch (error: unknown) {
        const apiMessage =
          typeof error === 'object' &&
          error !== null &&
          'data' in error &&
          typeof (error as { data?: { message?: string } }).data?.message ===
            'string'
            ? (error as { data: { message: string } }).data.message
            : undefined;

        const message = apiMessage ?? SUPPORT_ERROR_LEAD;

        setSubmitError(message);
        handleToast({
          step: 4,
          action: 'envio da nova senha',
          helpText: message + supportContactPlain,
        });
      }
    },
    [atualizarCadastro, handleClose, handleToast],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg="white">
        <ModalHeader
          fontSize="xl"
          fontWeight={500}
          color="black"
          pr="48px"
        >
          Receber nova senha por e-mail
        </ModalHeader>
        <ModalCloseButton color="black" />
        <ModalBody color="black">
          <Text mb={2} fontSize="md" color="black" fontWeight="semibold">
            O acesso ao Portal foi atualizado
          </Text>
          <Text mb={4} fontSize="sm" color="black" opacity={0.85} lineHeight="tall">
            Informe o e-mail cadastrado no Portal junto com seu CPF. Enviaremos
            uma senha provisória para esse endereço — use-a para entrar e, em
            seguida, crie uma nova senha.
          </Text>
          <VStack spacing={4} align="stretch">
            <SimpleInput
              {...register('email')}
              id="email-cadastral"
              label="E-mail cadastrado no Portal"
              labelColor="black"
              placeholder="Digite o e-mail que você usa no Portal"
              error={errors.email?.message?.toString()}
              bg="transparent"
            />
            <SimpleInput
              {...register('cpf')}
              id="cpf-cadastral"
              label="CPF"
              labelColor="black"
              type="cpf"
              placeholder="Digite seu CPF"
              error={errors.cpf?.message?.toString()}
              bg="transparent"
            />
          </VStack>
          {submitError && (
            <Alert status="error" mt={4} borderRadius="md" fontSize="sm">
              <Text>
                {submitError}
                <SupportContactLinks />
              </Text>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="orange"
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            Enviar senha por e-mail
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
